import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from './Header';
import SEO from './SEO';
import { brandedProductImageUrl } from '../utils/imageUrls';
import { productIdFromSlug, productPath } from '../utils/productUrls';
import './ProductDetail.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

function statusLabel(product) {
  if (product?.acne_safety_status === 'unknown_ingredients') return 'Unknown acne safety';
  if (product?.is_acne_safe === true) return 'Acne safe';
  if (product?.acne_safety_status === 'not_acne_safe') return 'Not acne safe';
  return 'Unknown acne safety';
}

function statusClass(product) {
  if (product?.is_acne_safe === true) return 'is-safe';
  if (product?.acne_safety_status === 'not_acne_safe') return 'is-not-safe';
  return 'is-unknown';
}

function ingredientLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (item.flagged_ingredient) {
    const matched = Array.isArray(item.product_ingredients) ? item.product_ingredients.join(', ') : '';
    return matched ? `${item.flagged_ingredient}: ${matched}` : item.flagged_ingredient;
  }
  if (item.name) return item.name;
  return String(item);
}

function readableList(items) {
  return Array.isArray(items) ? items.map(ingredientLabel).filter(Boolean) : [];
}

function productDescription(product) {
  if (product.short_summary) return product.short_summary;

  const status = statusLabel(product).toLowerCase();
  const brand = product.brand ? `${product.brand} ` : '';
  return `${brand}${product.name} is listed as ${status} in the Acne Safe product database. View product type, ingredients, and flagged pore-clogging ingredients.`;
}

function productSelectFields(includeDescriptions = true) {
  const fields = [
    'id',
    'retailer',
    'brand',
    'name',
    'category',
    'product_type',
    'url',
    'ingredients',
    'is_acne_safe',
    'acne_safety_status',
    'unsafe_ingredients',
    'matched_product_ingredients',
    'stored_image_url',
    'image_url',
  ];

  if (includeDescriptions) {
    fields.push('short_summary', 'product_overview');
  }

  return fields.join(',');
}

function parseOverviewBlocks(overview) {
  const lines = (overview || '').split(/\n+/).map((item) => item.trim()).filter(Boolean);
  const blocks = [];
  let currentList = null;

  lines.forEach((line) => {
    if (/^(Key Benefits|Key Ingredients):?$/i.test(line)) {
      currentList = { type: 'list', title: line.replace(/:$/, ''), items: [] };
      blocks.push(currentList);
      return;
    }

    if (/^Please note:/i.test(line)) {
      currentList = null;
      blocks.push({
        type: 'note',
        title: 'Please note',
        text: line.replace(/^Please note:\s*/i, ''),
      });
      return;
    }

    if (currentList) {
      currentList.items.push(line);
    } else {
      blocks.push({ type: 'paragraph', text: line });
    }
  });

  return blocks;
}

function buildProductUrl(product) {
  return `https://www.useacnesafe.com${productPath(product)}`;
}

function retailerLabel(product) {
  const retailer = (product?.retailer || '').toLowerCase();
  const url = product?.url || '';
  if (retailer === 'sephora' || url.includes('sephora.com')) return 'Sephora';
  if (retailer === 'dermstore' || url.includes('dermstore.com')) return 'Dermstore';
  if (retailer === 'ulta' || url.includes('ulta.com')) return 'Ulta';
  if (retailer === 'target' || url.includes('target.com')) return 'Target';
  if (retailer === 'walgreens' || url.includes('walgreens.com')) return 'Walgreens';
  if (retailer === 'face_reality') return 'Face Reality';
  return 'retailer';
}

function ProductDetail() {
  const { slug } = useParams();
  const productId = productIdFromSlug(slug);
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      setIsLoading(false);
      return;
    }

    if (!productId) {
      setError('Product not found.');
      setIsLoading(false);
      return;
    }

    let isCurrent = true;

    async function fetchProduct() {
      setIsLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('select', productSelectFields(true));
        params.set('id', `eq.${productId}`);
        params.set('limit', '1');

        let response = await fetch(`${SUPABASE_URL}/rest/v1/products?${params.toString()}`, {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
        });

        if (response.status === 400) {
          params.set('select', productSelectFields(false));
          response = await fetch(`${SUPABASE_URL}/rest/v1/products?${params.toString()}`, {
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
          });
        }

        if (!response.ok) {
          throw new Error(`Product lookup failed (${response.status})`);
        }

        const rows = await response.json();
        if (!isCurrent) return;

        if (!rows[0]) {
          setError('Product not found.');
        } else {
          setProduct(rows[0]);
        }
      } catch (err) {
        if (isCurrent) setError(err.message || 'Product lookup failed.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    }

    fetchProduct();
    return () => {
      isCurrent = false;
    };
  }, [productId, slug]);

  const ingredients = useMemo(() => readableList(product?.ingredients), [product]);
  const unsafeIngredients = useMemo(() => readableList(product?.unsafe_ingredients), [product]);
  const overviewBlocks = useMemo(() => parseOverviewBlocks(product?.product_overview), [product]);

  if (isLoading) {
    return (
      <div className="App">
        <Header />
        <main className="product-detail-page">
          <p className="product-detail-message">Loading product...</p>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="App">
        <SEO title="Product Not Found | Acne Safe" description="This product could not be found in the Acne Safe product database." />
        <Header />
        <main className="product-detail-page">
          <Link to="/products" className="back-to-products">All products</Link>
          <section className="product-detail-message">{error || 'Product not found.'}</section>
        </main>
      </div>
    );
  }

  const originalImageUrl = product.stored_image_url || product.image_url;
  const imageUrl = brandedProductImageUrl(originalImageUrl) || '/hero-image.png';
  const canonicalUrl = buildProductUrl(product);
  const title = `${product.brand ? `${product.brand} ` : ''}${product.name} Acne Safety`;
  const description = productDescription(product);
  const retailerName = retailerLabel(product);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
    category: product.product_type || product.category,
    image: imageUrl,
    description,
    url: canonicalUrl,
    sameAs: product.url || undefined,
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'Acne safety', value: statusLabel(product) },
      { '@type': 'PropertyValue', name: 'Retailer', value: retailerName },
    ],
  };

  return (
    <div className="App">
      <SEO
        title={title}
        description={description}
        keywords={`${product.name}, ${product.brand || ''}, acne safe product, non comedogenic, pore clogging ingredients`}
        image={imageUrl}
        url={canonicalUrl}
        type="product"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />
      <main className="product-detail-page">
        <Link to="/products" className="back-to-products">All products</Link>

        <article className="product-detail-shell">
          <section className="product-detail-image-panel">
            <img
              src={imageUrl}
              alt={`${product.brand || ''} ${product.name}`.trim()}
              loading="eager"
              decoding="async"
              onError={(event) => {
                if (originalImageUrl && event.currentTarget.src !== originalImageUrl) {
                  event.currentTarget.src = originalImageUrl;
                }
              }}
            />
          </section>

          <section className="product-detail-content">
            <div className="product-detail-badges">
              <span className={`product-status ${statusClass(product)}`}>{statusLabel(product)}</span>
              {product.product_type && <span className="product-type-label">{product.product_type}</span>}
            </div>

            {product.brand && <p className="product-detail-brand">{product.brand}</p>}
            <h1>{product.name}</h1>
            {product.short_summary && <p className="product-detail-summary">{product.short_summary}</p>}
            {product.category && <p className="product-detail-category">{product.category}</p>}

            <div className="product-detail-actions">
              {product.url && (
                <a href={product.url} target="_blank" rel="noopener noreferrer">
                  View on {retailerName}
                </a>
              )}
            </div>
          </section>
        </article>

        <section className="product-detail-grid">
          {overviewBlocks.length > 0 && (
            <div className="product-detail-section is-wide">
              <h2>Product Overview</h2>
              <div className="product-overview-content">
                {overviewBlocks.map((block, index) => {
                  if (block.type === 'list') {
                    return (
                      <div className="product-overview-group" key={`${block.title}-${index}`}>
                        <h3>{block.title}</h3>
                        <ul>
                          {block.items.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }

                  if (block.type === 'note') {
                    return (
                      <div className="product-overview-note" key={`${block.title}-${index}`}>
                        <h3>{block.title}</h3>
                        <p>{block.text}</p>
                      </div>
                    );
                  }

                  return <p key={`${block.text}-${index}`}>{block.text}</p>;
                })}
              </div>
            </div>
          )}

          <div className="product-detail-section">
            <h2>Flagged Ingredients</h2>
            {unsafeIngredients.length > 0 ? (
              <ul className="ingredient-chip-list">
                {unsafeIngredients.map((ingredient) => (
                  <li key={ingredient}>{ingredient}</li>
                ))}
              </ul>
            ) : (
              <p>No pore-clogging ingredients found.</p>
            )}
          </div>

          <div className="product-detail-section">
            <h2>Full Ingredient List</h2>
            {ingredients.length > 0 ? (
              <p className="ingredient-copy">{ingredients.join(', ')}</p>
            ) : (
              <p>Ingredient list not available for this product.</p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ProductDetail;
